<script lang="ts">
  import { getContext } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Loading from '../etc/loader.svelte';
  import ContactChange from '../etc/modal/ContactChangeModal.svelte';
  import PaymentChange from '../etc/modal/PaymentChangeModal.svelte';
  import MonthlySubscriptionInfoTypeModal from '../etc/modal/hikari01/contract/ModifyMonthlySubscriptionInfoModal.svelte';
  import PaymentChangeResultModal from '../etc/modal/PaymentChangeResultModal.svelte';
  import RepositoryFactory, { CUSTOMER, PAYINFO } from '../repositories/RepositoryFactory';
  import type { GetCustomerResult } from '../repositories/customer';
  import { querystring } from 'svelte-spa-router';
  import { loginUser } from '../components/auth/LoginUser';
  import { monthlySubscriptionInfoTypes } from '../constants/MonthlySubscriptionInfoType';

  const { open }: any = getContext('simple-modal');
  const businessCode = $loginUser.businessCode;

  let customerinfo: GetCustomerResult;
  let paytypeCode: string;
  let promise = getApi();

  async function getApi() {
    const payinfoRepository = RepositoryFactory[PAYINFO];

    if ($querystring.includes('EncryptValue=')) {
      const encryptValue: string = $querystring.substring($querystring.indexOf('?EncryptValue=') + 14);
      await payinfoRepository
        .cardChangeAfter({
          encryptValue
        })
        .then((res) => {
          if (!res) {
            throw new Error();
          }
          showPaymentChangeResultModal(res.responseMsg, res.reopenedList);
        })
        .catch((error) => {
          if (error.response.status === 403) {
            alert('セッションが切れました。再度ログインしてください。');
            loginUser.logout();
            push('/login');
          } else {
            alert('エラーが発生しました。');
          }
          return false;
        });

      // URLからクエリパラメータを削除し履歴エントリー更新
      const url = location.href;
      const afterUrl = url.substring(0, url.indexOf('?EncryptValue='));
      history.replaceState('', '', afterUrl);
    }

    const customerRepository = RepositoryFactory[CUSTOMER];
    await customerRepository
      .getCustomer()
      .then((res) => {
        if (!res) {
          throw new Error();
        }
        customerinfo = res;
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください。');
          loginUser.logout();
          push('/login');
        } else {
          alert('エラーが発生しました。');
        }
      });
    return { customerinfo };
  }

  const showContactChange = () => {
    open(ContactChange, {});
  };
  const showPaymentChange = (code: string) => {
    open(PaymentChange, { paytypeCode: code });
    paytypeCode = code;
  };

  const showPaymentChangeResultModal = (responseMsg: string, reopenedList: string[]): void => {
    open(
      PaymentChangeResultModal,
      {
        responseMsg: responseMsg,
        reopenedList: reopenedList
      },
      {
        closeButton: false,
        closeOnEsc: false,
        closeOnOuterClick: false
      }
    );
  };

  const getMonthlySubscriptionInfoTypeName = (monthlySubscriptionInfoType: string) => {
    return monthlySubscriptionInfoTypes.find((v) => v.code === monthlySubscriptionInfoType).name;
  };

  const showModifyMonthlySubscriptionInfo = () => {
    open(MonthlySubscriptionInfoTypeModal, {});
  };

  const isDisabledPaymentChange = () => {
    if (customerinfo.contract.contractStatus === '解約済') {
      return true;
    }
    if (customerinfo.payinfo.paytypeCode === 'OEM') {
      return true;
    }
    return false;
  };
</script>

{#await promise}
  <Loading />
{:then { customerinfo }}
  <div class="mb-5 screen-title">
    <span class="text-gray-700 text-2xl font-bold">契約者情報</span>
  </div>
  <div class="flex mb-4">
    <div class="shadow rounded bg-white border-t-4 border-green-400">
      <div class="flex flex-col">
        {#if businessCode !== 'UMOBILE_D_ENECOM'}
          <div class="justify-items-center p-4">
            <div class="border-4 bg-red-100 border-red-500 text-red-500 px-12 py-4" role="alert">
              <p class="text-sm font-semibold">
                お支払い方法としてクレジットカードをご利用いただくにあたっての大切なご連絡→<a
                  href="pdf/mypage_information_3ds_202502.pdf"
                  class="underline"
                  rel="noopener noreferrer"
                  target="_blank">こちら</a
                >を参照ください
              </p>
            </div>
          </div>
        {/if}
        <div class="flex-1">
          <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4 mobile-flex-column">
            <div class="grid-1 col-span-2 justify-self-start w-full">
              <div class="grid md:grid-cols-2 self-center mb-4">
                <span class="grid-1 label-title">契約名義人</span>
                <span class="grid-1 label-value">
                  {customerinfo.customer.customerSei}{customerinfo.customer.customerMei}<br />
                  {customerinfo.customer.customerSeiKana}{customerinfo.customer.customerMeiKana}
                </span>
              </div>
              <div class="grid md:grid-cols-2 self-center">
                <span class="grid-1 label-title">連絡先住所</span>
                <span class="grid-1 label-value">
                  {customerinfo.customer.zipcode}
                  {customerinfo.customer.prefecture}
                  {customerinfo.customer.city}{customerinfo.customer.street}
                  {customerinfo.customer.building ? customerinfo.customer.building : ''}
                </span>
              </div>
            </div>
            <div class="grid-1 button-container">
              <button
                on:click={() => push(`/customer/change/`)}
                class="button-primary"
                disabled={customerinfo.contract.contractStatus === '解約済'}
              >
                変更
              </button>
            </div>
          </div>
        </div>

        <div class="flex-1">
          <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
            <div class="grid-1 col-span-2 justify-self-start w-full">
              <div class="grid md:grid-cols-2 self-center">
                <span class="grid-1 label-title">性別</span>
                <span class="grid-1 label-value">{customerinfo.customer.sex}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1">
          <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
            <div class="grid-1 col-span-2 justify-self-start w-full">
              <div class="grid md:grid-cols-2 self-center">
                <span class="grid-1 label-title">生年月日</span>
                <span class="grid-1 label-value">
                  {customerinfo.customer.birthDate}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1">
          <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4 mobile-flex-column">
            <div class="grid-1 col-span-2 justify-self-start w-full">
              <div class="grid md:grid-cols-2 self-center mb-4">
                <span class="grid-1 label-title">連絡先電話番号</span>
                <span class="grid-1 label-value">
                  {customerinfo.customer.telNo}
                </span>
              </div>
              <div class="grid md:grid-cols-2 self-center mb-4">
                <span class="grid-1 label-title">連絡先メールアドレス</span>
                <span class="grid-1 label-value">{customerinfo.customer.email ?? '-'}</span>
              </div>
            </div>
            <div class="grid-1 button-container">
              <button
                on:click={showContactChange}
                class="button-primary"
                disabled={customerinfo.contract.contractStatus === '解約済'}
              >
                変更
              </button>
            </div>
          </div>
        </div>

        <div class="flex-1">
          <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4 mobile-flex-column">
            <div class="grid-1 col-span-2 justify-self-start w-full">
              <div class="grid md:grid-cols-2 self-center">
                <span class="grid-1 label-title">お支払い方法</span>
                <span class="grid-1 label-value">
                  {customerinfo.payinfo.paytypeName}
                </span>
              </div>
            </div>
            {#if businessCode !== 'UMOBILE_D_ENECOM'}
              <div class="grid-1 button-container">
                <button
                  on:click={() => showPaymentChange(customerinfo.payinfo.paytypeCode)}
                  class="button-primary"
                  disabled={isDisabledPaymentChange()}
                >
                  変更
                </button>
              </div>
            {/if}
          </div>
        </div>
        {#if businessCode === 'HIKARI_01'}
          <div class="flex-1">
            <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4 mobile-flex-column">
              <div class="grid-1 col-span-2 justify-self-start w-full">
                <div class="grid md:grid-cols-2 self-center">
                  <span class="grid-1 label-title">請求額確定の通知方法</span>
                  <span class="grid-1 label-value">
                    {getMonthlySubscriptionInfoTypeName(customerinfo.contract.monthlySubscriptionInfoType)}
                  </span>
                </div>
              </div>
              <div class="grid-1 button-container">
                <button
                  on:click={showModifyMonthlySubscriptionInfo}
                  class="button-primary"
                  disabled={customerinfo.contract.monthlySubscriptionInfoType === 'NOT_AVAILABLE' ||
                    customerinfo.contract.contractStatus === '解約済'}
                >
                  変更
                </button>
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/await}

<style lang="postcss">
  .button-primary {
    @apply bg-green-400 rounded-lg hover:bg-green-500 focus:outline-none text-sm font-semibold text-white px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default bg-gray-400;
  }
  .label-title {
    @apply text-gray-900 font-semibold;
  }
  .label-value {
    @apply text-gray-900;
  }
  .screen-title {
    text-align: left;
  }
  @media screen and (max-width: 640px) {
    .screen-title {
      text-align: center;
    }
  }
  @media screen and (max-width: 640px) {
    .mobile-flex-column {
      display: flex;
      flex-direction: column;
    }
  }
  @media screen and (max-width: 640px) {
    .button-container {
      margin-top: 10px;
    }
  }
</style>
